import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import { Box, Typography, Paper, Grid, Divider, Tooltip } from '@material-ui/core';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import TrendingDownIcon from '@material-ui/icons/TrendingDown';
import StarIcon from '@material-ui/icons/Star';
import StarHalfIcon from '@material-ui/icons/StarHalf';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import CommentIcon from '@material-ui/icons/Comment';
import PeopleIcon from '@material-ui/icons/People';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  section: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
  },
  ratingItem: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows[3],
      borderColor: theme.palette.primary.light,
    },
  },
  ratingBar: {
    height: '12px',
    borderRadius: '6px',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    transition: 'width 0.5s ease-in-out',
  },
  ratingHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  ratingTitle: {
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: theme.spacing(1),
    },
  },
  ratingValue: {
    display: 'flex',
    alignItems: 'center',
    '& svg': {
      marginRight: theme.spacing(0.5),
    },
  },
  ratingStats: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(2),
    '& svg': {
      marginRight: theme.spacing(0.5),
      fontSize: '1rem',
    },
  },
  starRating: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  star: {
    color: theme.palette.warning.main,
  },
  distributionContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  distributionBar: {
    flex: 1,
    height: '8px',
    margin: '0 2px',
    borderRadius: '4px',
    transition: 'all 0.3s ease',
  },
  metricCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  metricValue: {
    fontWeight: 'bold',
    fontSize: '1.5rem',
  },
  metricLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  polarizingBadge: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1),
  },
  consistencyBadge: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    fontSize: '0.75rem',
    marginLeft: theme.spacing(1),
  }
}));

// Helper function to render star rating
const renderStarRating = (rating, maxRating = 5, classes) => {
  const fullStars = Math.floor(rating);
  const hasHalfStar = rating % 1 >= 0.5;
  const emptyStars = Math.floor(maxRating - rating - (hasHalfStar ? 1 : 0));
  
  return (
    <Box className={classes.starRating}>
      {[...Array(fullStars)].map((_, i) => (
        <StarIcon key={`full-${i}`} className={classes.star} />
      ))}
      {hasHalfStar && <StarHalfIcon className={classes.star} />}
      {[...Array(emptyStars)].map((_, i) => (
        <StarBorderIcon key={`empty-${i}`} className={classes.star} />
      ))}
    </Box>
  );
};

// Helper function to determine color based on rating value
const getRatingColor = (rating, average) => {
  if (rating >= average + 1) return '#4CAF50'; // Much above average
  if (rating > average) return '#8BC34A';      // Above average
  if (rating === average) return '#FFC107';    // Average
  if (rating >= average - 1) return '#FF9800'; // Below average
  return '#F44336';                            // Much below average
};

const MultipleRatingVisualization = ({ data = {}, metrics = {} }) => {
  const classes = useStyles();
  
  // Extract and prepare data with safe defaults
  const ratingItems = data?.items || [];
  const totalResponses = metrics?.totalResponses || 0;
  const responseRate = metrics?.responseRate || 0;
  const averageRating = metrics?.averageRating || 0;
  const userSegments = metrics?.userSegments || {};
  const platformDistribution = metrics?.platformDistribution || {};
  const polarizingItems = metrics?.polarizingItems || [];
  const ratingConsistency = metrics?.ratingConsistency || 'Unknown';
  
  // Sort items by average rating (highest first)
  const sortedItems = [...ratingItems].sort((a, b) => (b.averageRating || 0) - (a.averageRating || 0));
  
  // Calculate overall metrics
  const highestRatedItem = sortedItems.length > 0 ? sortedItems[0] : null;
  const lowestRatedItem = sortedItems.length > 0 ? sortedItems[sortedItems.length - 1] : null;
  const ratingSpread = highestRatedItem && lowestRatedItem 
    ? (highestRatedItem.averageRating || 0) - (lowestRatedItem.averageRating || 0)
    : 0;
  
  console.log('Rendering MultipleRatingVisualization with:', {
    hasData: !!data,
    hasMetrics: !!metrics,
    itemsCount: ratingItems.length,
    totalResponses,
    responseRate,
    averageRating,
    timestamp: new Date().toISOString()
  });

  return (
    <Box className={classes.root}>
      {/* Overview Metrics */}
      <Paper className={classes.section}>
        <Typography variant="h6" gutterBottom>
          Rating Overview
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={1} className={classes.metricCard}>
              <Typography className={classes.metricValue}>{totalResponses}</Typography>
              <Typography className={classes.metricLabel}>Total Responses</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={1} className={classes.metricCard}>
              <Typography className={classes.metricValue}>{responseRate}%</Typography>
              <Typography className={classes.metricLabel}>Response Rate</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={1} className={classes.metricCard}>
              <Typography className={classes.metricValue}>{averageRating.toFixed(1)}</Typography>
              <Typography className={classes.metricLabel}>Overall Average Rating</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper elevation={1} className={classes.metricCard}>
              <Typography className={classes.metricValue}>{ratingItems.length}</Typography>
              <Typography className={classes.metricLabel}>Rated Items</Typography>
            </Paper>
          </Grid>
        </Grid>
        
        <Box mt={2}>
          <Typography variant="subtitle1" gutterBottom>
            Rating Consistency: 
            <span className={classes.consistencyBadge}>{ratingConsistency}</span>
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Rating Spread: {ratingSpread.toFixed(1)} points
          </Typography>
        </Box>
      </Paper>

      {/* Rated Items */}
      {sortedItems.length > 0 && (
        <Paper className={classes.section}>
          <Typography variant="h6" gutterBottom>
            Rated Items (Sorted by Rating)
          </Typography>
          
          {sortedItems.map((item, index) => {
            const isPolarizing = polarizingItems.includes(item.title);
            const ratingColor = getRatingColor(item.averageRating || 0, averageRating);
            const normalizedRating = Math.min(Math.max((item.averageRating || 0) / 5, 0), 1) * 100;
            
            return (
              <Box key={index} className={classes.ratingItem}>
                <Box className={classes.ratingHeader}>
                  <Typography variant="h6" className={classes.ratingTitle}>
                    {index + 1}. {item.title}
                    {isPolarizing && (
                      <Tooltip title="This item has polarizing ratings with significant high and low ratings">
                        <span className={classes.polarizingBadge}>Polarizing</span>
                      </Tooltip>
                    )}
                  </Typography>
                  <Typography variant="h6" className={classes.ratingValue}>
                    {(item.averageRating || 0).toFixed(1)}
                    <Typography variant="body2" color="textSecondary" component="span" style={{ marginLeft: 8 }}>
                      / 5
                    </Typography>
                  </Typography>
                </Box>
                
                {renderStarRating(item.averageRating || 0, 5, classes)}
                
                <Box 
                  className={classes.ratingBar} 
                  style={{ 
                    width: `${normalizedRating}%`,
                    backgroundColor: ratingColor
                  }} 
                />
                
                <Typography variant="body2" color="textSecondary">
                  Distribution:
                </Typography>
                
                <Box className={classes.distributionContainer}>
                  {[1, 2, 3, 4, 5].map(value => {
                    const count = item.distribution && item.distribution[value] 
                      ? item.distribution[value] 
                      : 0;
                    const percentage = totalResponses > 0 ? (count / totalResponses) * 100 : 0;
                    
                    return (
                      <Tooltip 
                        key={value} 
                        title={`${value} stars: ${count} responses (${percentage.toFixed(1)}%)`}
                      >
                        <Box 
                          className={classes.distributionBar}
                          style={{ 
                            height: `${Math.max(percentage, 5)}px`, 
                            backgroundColor: getRatingColor(value, 3),
                            maxHeight: '30px'
                          }} 
                        />
                      </Tooltip>
                    );
                  })}
                </Box>
                
                <Box className={classes.ratingStats}>
                  <Box className={classes.statItem}>
                    <TrendingUpIcon color="primary" />
                    <Typography variant="body2">
                      High: {item.highRatingPercentage || 0}%
                    </Typography>
                  </Box>
                  <Box className={classes.statItem}>
                    <TrendingDownIcon color="error" />
                    <Typography variant="body2">
                      Low: {item.lowRatingPercentage || 0}%
                    </Typography>
                  </Box>
                  <Box className={classes.statItem}>
                    <CommentIcon color="action" />
                    <Typography variant="body2">
                      Comments: {item.comments || 0}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}

      {/* User Segments */}
      {Object.keys(userSegments).length > 0 && (
        <Paper className={classes.section}>
          <Typography variant="h6" gutterBottom>
            User Segments
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(userSegments).map(([key, value]) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Box className={classes.statItem}>
                  <PeopleIcon color="primary" />
                  <Typography variant="body1">
                    {key.replace(/([A-Z])/g, ' $1')
                      .replace(/^./, str => str.toUpperCase())
                      .replace('Users', '')}: {value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {/* Platform Distribution */}
      {Object.keys(platformDistribution).length > 0 && (
        <Paper className={classes.section}>
          <Typography variant="h6" gutterBottom>
            Platform Distribution
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(platformDistribution).map(([key, value]) => (
              <Grid item xs={12} sm={6} md={3} key={key}>
                <Typography variant="body1">
                  {key.replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())}: {value}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

MultipleRatingVisualization.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        averageRating: PropTypes.number,
        highRatingPercentage: PropTypes.number,
        lowRatingPercentage: PropTypes.number,
        distribution: PropTypes.object,
        comments: PropTypes.number,
      })
    ),
  }),
  metrics: PropTypes.shape({
    totalResponses: PropTypes.number,
    responseRate: PropTypes.number,
    averageRating: PropTypes.number,
    userSegments: PropTypes.object,
    platformDistribution: PropTypes.object,
    polarizingItems: PropTypes.array,
    ratingConsistency: PropTypes.string,
  }),
};

export default MultipleRatingVisualization;
